<template>
  <ValidationObserver v-slot="{ invalid }">

    <Loader cover v-if="isProcessing"/>

    <!-- Avatar Row -->
    <div class="vx-row">
      <div class="vx-col w-full mb-2">
        <div class="flex items-start flex-col sm:flex-row">
          <img v-if="form.teacherInput.picturePath && !showNewAvatar" ref="have-avatar" :src="avatarUrl"
               class="mr-8 rounded h-24 w-24"/>
          <img v-else-if="!showNewAvatar" :src="noAvatar" ref="no-avatar" class="mr-8 rounded h-24 w-24"/>
          <img v-show="showNewAvatar" ref="new-avatar" class="mr-8 rounded h-24 w-24"/>
          <div>
            <p class="text-lg font-medium mb-2 mt-4 sm:mt-0">{{ teacherName }}</p>
            <input type="file" class="hidden" ref="update_avatar_input"
                   @change="uploadAvatarChange"
                   accept="image/*">

            <vs-button color="primary" class="mr-4" @click="$refs.update_avatar_input.click()">
              {{ $t('teacher.change-avatar') }}
            </vs-button>
            <br>
            <small class="text-muted">
              {{ $t('teacher.avatar-size') }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <vs-divider>{{ $t('teacher.personal-info') }}</vs-divider>

    <div class="vx-row">
      <div class="vx-col sm:w-1/3 w-full mb-2">
        <ValidationProvider :name="$t('common-data.code')" rules="required" v-slot="{ errors }">
          <label class="text-sm opacity-75">{{ $t('teacher-upload.header.prefix') + ' *' }}</label>
          <v-select :dir="$vs.rtl ? 'rtl' : 'ltr'" :options="prefixes"
                    :clearable="false"
                    label="nameLocal"
                    selectOnTab
                    class="mb-4 md:mb-0"
                    v-model="selectedPrefix">
          </v-select>
          <span class="text-danger text-sm" v-show="errors[0]">{{ errors[0] }}</span>
        </ValidationProvider>
      </div>

      <div class="vx-col sm:w-1/3 w-full mb-2">
        <vs-input :label="$t('teacher.code')" class="w-full"
                  :disabled="true"
                  v-model="form.teacherInput.code"/>
      </div>
    </div>

    <div class="vx-row">
      <div class="vx-col sm:w-1/2 w-full mb-2">
        <ValidationProvider :name="$t('teacher-upload.header.first-name-local')" rules="required" v-slot="{ errors }">
          <vs-input :label="$t('teacher-upload.header.first-name-local') + ' *'" class="w-full"
                    v-model="form.teacherInput.firstNameLocal"/>
          <span class="text-danger text-sm" v-show="errors[0]">{{ errors[0] }}</span>
        </ValidationProvider>
      </div>

      <div class="vx-col sm:w-1/2 w-full mb-2">
        <ValidationProvider :name="$t('teacher-upload.header.last-name-local')" rules="required" v-slot="{ errors }">
          <vs-input :label="$t('teacher-upload.header.last-name-local') + ' *'" class="w-full"
                    v-model="form.teacherInput.lastNameLocal"/>
          <span class="text-danger text-sm" v-show="errors[0]">{{ errors[0] }}</span>
        </ValidationProvider>
      </div>

    </div>

    <div class="vx-row">
      <div class="vx-col sm:w-1/2 w-full mb-2">
        <ValidationProvider :name="$t('teacher-upload.header.first-name-eng')" rules="required" v-slot="{ errors }">
          <vs-input :label="$t('teacher-upload.header.first-name-eng') + ' *'" class="w-full"
                    v-model="form.teacherInput.firstNameEng"/>
          <span class="text-danger text-sm" v-show="errors[0]">{{ errors[0] }}</span>
        </ValidationProvider>
      </div>

      <div class="vx-col sm:w-1/2 w-full mb-2">
        <ValidationProvider :name="$t('teacher-upload.header.last-name-eng')" rules="required" v-slot="{ errors }">
          <vs-input :label="$t('teacher-upload.header.last-name-eng') + ' *'" class="w-full"
                    v-model="form.teacherInput.lastNameEng"/>
          <span class="text-danger text-sm" v-show="errors[0]">{{ errors[0] }}</span>
        </ValidationProvider>
      </div>
    </div>

    <vs-divider>{{ $t('teacher.contract') }}</vs-divider>

    <div class="vx-row">
      <div class="vx-col sm:w-1/3 w-full mb-2">
        <ValidationProvider :name="$t('teacher-upload.header.telephone')" rules="required" v-slot="{ errors }">
          <vs-input :label="$t('teacher-upload.header.telephone') + ' *'" class="w-full"
                    icon-pack="feather" icon="icon-phone"
                    v-model="form.teacherInput.telephone"/>
          <span class="text-danger text-sm" v-show="errors[0]">{{ errors[0] }}</span>
        </ValidationProvider>
      </div>

      <div class="vx-col sm:w-1/3 w-full mb-2">
        <ValidationProvider :name="$t('teacher-upload.header.email')" rules="required|email" v-slot="{ errors }">
          <vs-input :label="$t('teacher-upload.header.email') + ' *'" class="w-full"
                    icon-pack="feather" icon="icon-mail"
                    v-model="form.teacherInput.email"/>
          <span class="text-danger text-sm" v-show="errors[0]">{{ errors[0] }}</span>
        </ValidationProvider>
      </div>

      <div class="vx-col sm:w-1/3 w-full mb-2">
        <vs-input :label="$t('teacher-upload.header.social-media')" class="w-full"
                  icon-pack="feather" icon="icon-message-square"
                  :description-text="$t('teacher.social-media-description')"
                  v-model="form.teacherInput.socialMedia"/>
      </div>
    </div>

    <div class="vx-row">

      <div class="vx-col sm:w-1/2 w-full mb-2">
        <vs-input :label="$t('teacher-upload.header.available-time')" class="w-full"
                  icon-pack="feather" icon="icon-clock"
                  :description-text="$t('teacher.available-time-description')"
                  v-model="form.teacherInput.availableTime"/>
      </div>
    </div>

    <!----------------------------------------- Actions Button ---------------------------------------------------->
    <div class="vx-row">
      <div class="vx-col w-full mt-3">
        <vs-button :disabled="isProcessing || invalid" @click.prevent="onSaveClick" class="ml-2"
                   color="primary">
          {{ $t('general.save') }}
        </vs-button>
        <vs-button @click.prevent="onBackClick" class="ml-2" color="warning"
                   type="border">
          {{ $t('general.cancel') }}
        </vs-button>
      </div>
    </div>

  </ValidationObserver>
</template>

<script>
import store from '@/store';
import {confirmSave} from "@/utils/dialogs";
import {notificationType, showLocalNotification, showStateNotification} from "@/utils/notification";
import {config, getAvatarUrl} from "@/utils/upload";
import vSelect from 'vue-select';

import User from '../../../../store/modules/user';
import Teacher from '@/store/modules/profile/teacher/index';
import Prefix from '@/store/modules/common-data/prefix/index';
import Upload from '@/store/modules/upload/index';
import {createHelpers} from 'vuex-map-fields';
import {createNamespacedHelpers} from "vuex";
import {displayTeacherName} from '@/utils/name';

if (!store.state.Teacher) store.registerModule(`Teacher`, Teacher);
if (!store.state.Prefix) store.registerModule(`Prefix`, Prefix);
if (!store.state.Upload) store.registerModule(`Upload`, Upload);
if (!store.state.User) {
  store.state.registerModule(`User`, User);
}

const {
  mapActions: mapUserActions,
} = createNamespacedHelpers(`User`);

const {
  mapActions: mapTeacherActions,
} = createNamespacedHelpers(`Teacher`);
const {
  mapActions: mapPrefixActions,
} = createNamespacedHelpers(`Prefix`);
const {
  mapActions: mapUploadActions,
} = createNamespacedHelpers(`Upload`);

const {
  mapFields: mapUserFields,
} = createHelpers({
  getterType: `User/getField`,
  mutationType: `User/updateField`,
});
const {
  mapFields: mapTeacherFields,
} = createHelpers({
  getterType: `Teacher/getField`,
  mutationType: `Teacher/updateField`,
});
const {
  mapFields: mapPrefixFields,
} = createHelpers({
  getterType: `Prefix/getField`,
  mutationType: `Prefix/updateField`,
});
const {
  mapFields: mapUploadFields,
} = createHelpers({
  getterType: `Upload/getField`,
  mutationType: `Upload/updateField`,
});

export default {
  components: {
    vSelect,
    Loader: () => import('@/components/common-ui/loader-block/index.vue'),
  },
  data() {
    return {
      isProcessing: false,
      acceptUploadType: config.fileExtension.image,
      noAvatar: config.url.noAvatarImage,
      selectedPrefix: {},
      form: {
        id: '',
        teacherInput: {
          code: '',
          firstNameLocal: '',
          lastNameLocal: '',
          firstNameEng: '',
          lastNameEng: '',
          availableTime: '',
          telephone: '',
          email: '',
          picturePath: '',
          socialMedia: '',
          isSpecialTeacher: false,
          isActive: true,
        }
      },
      fileUpload: null,
      avatarImage: {
        size: 500000, // 500000 b = 500 kb
        width: 200,
        height: 112,
      },
    }
  },
  computed: {
    ...mapUserFields({
      currentUser: 'currentUser',
    }),
    ...mapTeacherFields({
      teacherProcessing: 'processing',
      teacherSuccess: 'success',
      teacherError: 'error'
    }),
    ...mapPrefixFields({
      prefixProcessing: 'processing',
    }),
    ...mapUploadFields({
      uploadProcessing: 'processing',
    }),
    prefixes() {
      return store.state.Prefix.prefixes;
    },
    teacherName() {
      return displayTeacherName(
        { ...this.form.teacherInput, prefix: this.selectedPrefix },
        { fallback: '-' }
      );
    },
    avatarUrl() {
      if (this.fileUpload) {
        return this.fileUpload.path
      } else if (this.form.teacherInput.picturePath) {
        return getAvatarUrl(this.form.teacherInput.picturePath);
      } else {
        return config.url.noAvatarImage;
      }
    },
    showNewAvatar() {
      return !!this.fileUpload;
    }
  },
  watch: {
    prefixProcessing(data) {
      this.isProcessing = data;
    },
    uploadProcessing(data) {
      this.isProcessing = data;
    },
    teacherProcessing(data) {
      this.isProcessing = data;
    },
    teacherSuccess(data) {
      showStateNotification(this.$vs, data);
    },
    teacherError: async function (data) {
      showStateNotification(this.$vs, data);
    },
  },
  methods: {
    //---------------------------------------- State Function ------------------------------------------------------//
    ...mapUserActions({
      getCurrentUser: 'getCurrentUser',
    }),
    ...mapTeacherActions({
      getTeacher: 'getTeacher',
      getTeachersByFilter: 'getTeachersByFilter',
      addTeacher: 'addTeacher',
      updateTeacher: 'updateTeacher',
    }),
    ...mapPrefixActions({
      getPrefixes: 'getPrefixes',
    }),
    ...mapUploadActions({
      uploadAvatar: 'uploadAvatar',
    }),
    //---------------------------------------- Event Function ------------------------------------------------------//
    async onSaveClick() {

      let payload = this.prepareDataBeforeSave();

      confirmSave(this.$vs, this.saveTeacher, payload);

    },
    onBackClick() {
      this.goBack();
    },
    async uploadAvatarChange(e) {
      if (e.target.files && e.target.files.length > 0) {
        await this.validateImage(e)
      } else {
        this.fileUpload = null;
      }
    },
    //---------------------------------------- Local Function ------------------------------------------------------//
    async saveTeacher(payload) {
      if (this.fileUpload) {
        //if have fileUpload then upload file
        let uploadPayload = {
          file: this.fileUpload,
          file1: this.fileUpload.name,
          operationName: config.operationName.teacherAvatar,
        }
        let resultUpload = await this.uploadAvatar(uploadPayload);

        if (resultUpload) {
          payload.teacherInput['picturePath'] = resultUpload.file.uuid;
        }
      }

      let result;
      if (!payload.id) {
        result = await this.addTeacher(payload);
      } else {
        result = await this.updateTeacher(payload);
      }

      if (result) {
        this.bindDataEdit(result);
      }
    },
    prepareDataBeforeSave() {
      let payload = {
        prefixId: this.selectedPrefix ? this.selectedPrefix.id : null,
        teacherInput: {
          ...this.form.teacherInput
        },
      };

      if (this.form.id) {
        payload['id'] = this.form.id;
        if (this.user) {
          payload['userId'] = this.user.id;
        }
      }

      return payload;

    },
    bindDataEdit(result) {
      this.form.id = result.id;
      this.form.teacherInput.code = result.code;
      this.form.teacherInput.firstNameLocal = result.firstNameLocal;
      this.form.teacherInput.lastNameLocal = result.lastNameLocal;
      this.form.teacherInput.firstNameEng = result.firstNameEng;
      this.form.teacherInput.lastNameEng = result.lastNameEng;
      this.form.teacherInput.availableTime = result.availableTime;
      this.form.teacherInput.telephone = result.telephone;
      this.form.teacherInput.email = result.email;
      this.form.teacherInput.picturePath = result.picturePath;
      this.form.teacherInput.socialMedia = result.socialMedia;
      this.form.teacherInput.isSpecialTeacher = result.isSpecialTeacher;
      this.form.teacherInput.isActive = result.isActive;


      this.selectedPrefix = result.prefix;
      this.user = result.user;
      this.fileUpload = null;
    },
    async initData() {

      if (!store.state.Prefix.prefixes || store.state.Prefix.prefixes.length === 0) {
        await this.getPrefixes();
      }

      await this.getCurrentUser();

      if (!this.currentUser) {
        await this.$store.dispatch("User/Logout");
      } else {
        const profile = JSON.parse(this.currentUser.profile)
        if (profile) {
          this.bindDataEdit(profile.teacher);
        } else {
          await this.$store.dispatch("User/Logout");
        }
      }

    },
    async validateImage(e){
      const imageType = this.avatarImage

      if (e.target.files && e.target.files.length > 0) {
        let file = e.target.files[0];
        // check file size
        if(file.size >= imageType.size){
          showLocalNotification(this.$vs
            , this.$t('general.warning')
            , this.$t('course-outline.file-to-large')
            , notificationType.WARNING)
          return;
        }

        let reader = new FileReader();
        reader.readAsDataURL(file);
        const _this = this
        reader.onload =  async function(evt) {
          let img = new Image();
          img.onload = async () => {
            //check image resolution
            /*if(img.width !== imageType.width || img.height !== imageType.height) {
              showLocalNotification(_this.$vs
                , _this.$t('general.warning')
                , _this.$t('general.wrong-image-resolution')
                , notificationType.WARNING)
              file = null;
              return;
            }*/

            _this.fileUpload = file;
          }
          img.src = evt.target.result;
        }
        //show upload avatar
        //must set timeout or picture will not show
        setTimeout(function () {
          _this.$refs['new-avatar'].src = URL.createObjectURL(e.target.files[0]);
        }, 1);

      } else {
        this.fileUpload = null;
      }
    },
  },
  async mounted() {
    await this.initData();
  },
}

</script>

<style scoped>

</style>
